// Lightweight fetch client targeting the Healthy Horizons consumer API.
// Stores JWT + refresh token in localStorage. Auto-refreshes on 401.

// REACT_APP_API_BASE points at the external backend (healthyhorizons.clinic).
// Falls back to REACT_APP_BACKEND_URL for local/colocated development.
const BACKEND_URL = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const ACCESS_KEY = "voya9_token";
const REFRESH_KEY = "voya9_refresh";

export function getToken() {
  return localStorage.getItem(ACCESS_KEY);
}
export function getRefresh() {
  return localStorage.getItem(REFRESH_KEY);
}
export function setTokens(token, refreshToken) {
  if (token) localStorage.setItem(ACCESS_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
}
export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

let refreshing = null;
async function tryRefresh() {
  if (refreshing) return refreshing;
  const rt = getRefresh();
  if (!rt) return null;
  refreshing = (async () => {
    try {
      const r = await fetch(`${API_BASE}/consumer/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!r.ok) return null;
      const data = await r.json();
      setTokens(data.token, data.refreshToken);
      return data.token;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export class ApiError extends Error {
  constructor(status, detail, raw) {
    super(detail || `HTTP ${status}`);
    this.status = status;
    this.detail = detail;
    // Parsed response body. Exposed under both `raw` (legacy) and
    // `data` (intuitive for consumers reading structured error
    // payloads like `{ detail: { code, sku, variant } }`). Aliased so
    // existing call sites that reference either name keep working.
    this.raw = raw;
    this.data = raw;
  }
}

export async function api(path, { method = "GET", body, headers = {}, auth = true, retried = false, noAuthRedirect = false } = {}) {
  const token = auth ? getToken() : null;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && auth && !retried) {
    const newTok = await tryRefresh();
    if (newTok) {
      return api(path, { method, body, headers, auth, retried: true, noAuthRedirect });
    }
    // `noAuthRedirect` lets callers (e.g. soft-auth endpoints like the
    // research catalogue, which works for anonymous visitors too) opt
    // out of the global "kick to /sign-in" behaviour. They still
    // receive a 401 ApiError which they can handle locally.
    if (!noAuthRedirect) {
      clearTokens();
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/sign-in")) {
        window.location.href = "/sign-in";
      }
    }
  }

  let data = null;
  // Body read can fail on certain mobile browsers (Safari/iOS sometimes
  // throws "Body is disturbed or locked" when a network proxy peeks at
  // the response). Treat those as empty bodies so the caller still
  // gets a sane ApiError to act on rather than a cryptic raw TypeError.
  let text = "";
  try {
    text = await res.text();
  } catch (_) {
    text = "";
  }
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const detail = humanizeDetail(data) || friendlyStatusMessage(res.status, path);
    const err = new ApiError(res.status, detail, data);
    // Stash the raw response text on the error for debugging callers
    // that need to see exactly what the BE returned (especially when
    // `data` parsed to undefined because the body was empty / non-JSON).
    err.rawText = text || null;
    throw err;
  }
  return data;
}

// Status-aware fallback messages when the BE doesn't return a usable
// `detail` field. Consumers see these in toasts so they MUST read like
// human help, not like a stack trace. Auth endpoints get special copy
// because "404 not found" on a login attempt is a database lookup miss,
// not a missing page.
function friendlyStatusMessage(status, path = "") {
  const isAuthLogin = /\/(consumer|affiliate)\/auth\/login$/.test(path) || /\/sign-?in$/.test(path);
  const isPasswordReset = /forgot-password|reset-password/.test(path);
  if (status === 0 || !status) {
    return "We can't reach our server right now. Check your connection and try again.";
  }
  if (status === 401) {
    if (isAuthLogin) return "We couldn't sign you in with those details. Double-check your email and password, or reset it if you're stuck.";
    return "Your session expired. Please sign in again.";
  }
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) {
    if (isAuthLogin) return "We couldn't find an account with that email. Double-check it, or request access if you're new.";
    return "We couldn't find what you're looking for.";
  }
  if (status === 409) return "That conflicts with something already saved. Refresh and try again.";
  if (status === 422) return "Something in the form needs another look. Check the highlighted fields.";
  if (status === 429) {
    if (isPasswordReset) return "Too many reset attempts in a row. Take a breath and try again in a minute.";
    return "You're moving fast — give us a moment and try again.";
  }
  if (status >= 500) return "Something went wrong on our side. We're on it — try again in a moment.";
  // Fallback for anything else we haven't classified.
  return `We hit a snag (error ${status}). Try again in a moment.`;
}

// FastAPI/Pydantic 422 responses carry `detail` as an array of issue objects
// like [{ loc: ["body","email"], msg: "value is not a valid email address: ..." }].
// Naive `.detail` rendering produces "[object Object]" or falls back to a
// generic toast — neither helps the member fix their input. Humanize here.
function humanizeDetail(data) {
  if (!data) return null;
  const d = data.detail;
  if (!d) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    const parts = d.map((iss) => {
      if (typeof iss === "string") return iss;
      const field = Array.isArray(iss?.loc) ? iss.loc.filter((p) => p !== "body").join(".") : "";
      const msg = (iss?.msg || "").replace(/^value is not a valid /, "Invalid ");
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    if (parts.length > 0) return parts.join(" · ");
  }
  // Structured error envelope: `{ detail: { code: "...", message?: "...", ... } }`.
  // BE for /consumer/research/* returns this shape. We prefer `message`
  // when present, then map known `code` values to human copy, then
  // fall through to the status-aware fallback.
  if (typeof d === "object" && d !== null) {
    if (typeof d.message === "string" && d.message.trim()) return d.message;
    switch (d.code) {
      case "final_sale_ack_required":
        return "Please confirm you've read the final-sale notice before checking out.";
      case "no_price_for_variant": {
        const sku = d.sku ? ` for ${d.sku}` : "";
        const v = d.variant ? ` (${d.variant})` : "";
        return `Selected option${v} not available${sku}. Try the other variant.`;
      }
      case "sku_unavailable":
        return d.sku ? `${d.sku} is unavailable right now.` : "One of the selected compounds is unavailable.";
      default:
        return null; // let status-aware fallback take over
    }
  }
  return null;
}

// Convenience helpers
export const apiGet = (p, opts) => api(p, { ...opts, method: "GET" });
export const apiPost = (p, body, opts) => api(p, { ...opts, method: "POST", body });
export const apiPut = (p, body, opts) => api(p, { ...opts, method: "PUT", body });
export const apiDelete = (p, opts) => api(p, { ...opts, method: "DELETE" });
